import { Timestamp } from 'firebase/firestore';

export interface CustomerInfo {
  firstName: string;
  email: string;
  phone?: string;        // For SMS reminders and phone calls
  wechatId?: string;     // For WeChat communication
  message?: string;
  uploadedFiles?: string[]; // Array of Firebase Storage URLs
}

export interface AppointmentPreferences {
  addToCalendar: boolean;
  smsReminder: boolean;
  communicationChannel: 'zoom' | 'phone' | 'wechat';
}

export interface MeetingInfo {
  zoomUrl?: string;
  zoomMeetingId?: string;
  zoomPassword?: string;
  phoneNumber?: string;
  calendarEventId?: string;
}

export interface NotificationStatus {
  confirmationEmailSent: boolean;
  calendarInviteSent: boolean;
  reminderScheduled: boolean;
  smsReminderSent?: boolean;
}

export type AppointmentStatus = 'pending' | 'confirmed' | 'completed' | 'cancelled';

export interface Appointment {
  // No id field - Firestore document ID is separate from document data
  
  // Customer Information
  customerInfo: CustomerInfo;
  
  // Booking Details
  serviceType: string;
  scheduledDateTime: Timestamp;  // Combined date + time with timezone
  scheduledDate?: string;        // Human-readable date in LA timezone (e.g., "2025-12-29")
  scheduledTime?: string;        // Human-readable time in LA timezone (e.g., "10:00 AM")
  scheduledDateTimeLA?: string;  // Human-readable date+time in LA timezone (e.g., "2025-12-29 10:00 AM")
  timezone: string;              // Store original timezone for reference
  duration: number;              // minutes (default: 15)
  
  // Payment Information (for paid appointments)
  checkoutId?: string;           // Link to payment checkout record
  paymentStatus?: 'pending' | 'paid' | 'failed' | 'refunded';
  
  // Meeting Information (populated by triggers after creation)
  meeting?: MeetingInfo;
  
  // User Preferences
  preferences: AppointmentPreferences;
  
  // Status Tracking
  status: AppointmentStatus;
  notifications: NotificationStatus;
  
  // Metadata
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Service Types (matching your booking modal)
export const SERVICE_TYPES = {
  SCHOOL: 'school',
  LEGAL: 'legal', 
  REAL_ESTATE: 'realestate',
  HOME_MANAGEMENT: 'homemanagement',
  SPIRITUAL: 'spiritual',
  BUSINESS: 'business',
  BEVERAGE: 'beverage',
  CONCIERGE: 'concierge',
  UNSURE: 'unsure'
} as const;

export type ServiceType = typeof SERVICE_TYPES[keyof typeof SERVICE_TYPES];

export const SERVICE_LABELS: Record<ServiceType, string> = {
  [SERVICE_TYPES.SCHOOL]: 'School & Family Support',
  [SERVICE_TYPES.LEGAL]: 'Legal Support Coordination',
  [SERVICE_TYPES.REAL_ESTATE]: 'Real Estate & Relocation',
  [SERVICE_TYPES.HOME_MANAGEMENT]: 'Home Management',
  [SERVICE_TYPES.SPIRITUAL]: 'Spiritual Wellness',
  [SERVICE_TYPES.BUSINESS]: 'Business Consulting',
  [SERVICE_TYPES.BEVERAGE]: 'Beverage Development',
  [SERVICE_TYPES.CONCIERGE]: 'Elite Concierge',
  [SERVICE_TYPES.UNSURE]: 'Not sure yet'
};
